/**
 * CustomerReportPage — renders a persisted report from the customer's history.
 *
 * Route: /reports/:reportId (authenticated). Unlike the public ReportPage, this
 * loads the report from the database via the authenticated portal endpoint and
 * renders it without the phone-verification gate (the customer is already
 * signed in). The full report UI is shared via ReportView.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { customerAuthApi } from '../api/client'
import type { ReportPayload } from '../types'
import { useI18nNavigate } from '../hooks/useI18nNavigate'
import { ReportView, ReportLoading, ReportError } from './ReportPage'

export function CustomerReportPage() {
  const { reportId } = useParams<{ reportId: string }>()
  const navigate = useI18nNavigate()
  const { t } = useTranslation('report')
  const [report, setReport] = useState<ReportPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) { navigate('/login'); return }
    if (!reportId) return
    customerAuthApi
      .getReport(reportId)
      .then((detail) => setReport(detail.report))
      .catch(() => setError(t('error.notFound')))
      .finally(() => setLoading(false))
  }, [reportId, navigate, t])

  if (loading) return <ReportLoading />
  if (error || !report) return <ReportError message={error} />

  return <ReportView report={report} backPath="/dashboard" />
}
